import { useState, useEffect } from 'react';
import axios from 'axios';
import { Settings, Key, Bot, Scale, DollarSign, Save, Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSettings = () => {
  const [settings, setSettings] = useState({});
  const [botConfigs, setBotConfigs] = useState([]);
  const [scoringWeights, setScoringWeights] = useState({});
  const [budgetThresholds, setBudgetThresholds] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showKeys, setShowKeys] = useState({});
  const [saveStatus, setSaveStatus] = useState(null);

  useEffect(() => {
    fetchAllSettings();
  }, []);

  const fetchAllSettings = async () => {
    setLoading(true);
    try {
      const [settingsRes, botsRes, weightsRes, budgetsRes] = await Promise.all([
        axios.get(`${API}/admin/settings`),
        axios.get(`${API}/admin/bot-configs`),
        axios.get(`${API}/admin/deal-scoring-weights`),
        axios.get(`${API}/admin/budget-thresholds`)
      ]);
      
      setSettings(settingsRes.data);
      setBotConfigs(botsRes.data);
      setScoringWeights(weightsRes.data);
      setBudgetThresholds(budgetsRes.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveApiKeys = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, settings);
      setSaveStatus({ type: 'success', message: 'API keys saved successfully' });
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (error) {
      setSaveStatus({ type: 'error', message: 'Failed to save API keys' });
    } finally {
      setSaving(false);
    }
  };

  const saveScoringWeights = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/deal-scoring-weights`, scoringWeights);
      setSaveStatus({ type: 'success', message: 'Scoring weights saved' });
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (error) {
      setSaveStatus({ type: 'error', message: 'Failed to save scoring weights' });
    } finally {
      setSaving(false);
    }
  };

  const saveBudgetThresholds = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/budget-thresholds`, budgetThresholds);
      setSaveStatus({ type: 'success', message: 'Budget thresholds saved' });
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (error) {
      setSaveStatus({ type: 'error', message: 'Failed to save budget thresholds' });
    } finally {
      setSaving(false);
    }
  };

  const updateBotConfig = async (botId, field, value) => {
    try {
      const config = botConfigs.find(b => b.bot_id === botId) || { bot_id: botId };
      const updatedConfig = { ...config, [field]: value };
      
      await axios.put(`${API}/admin/bot-configs/${botId}`, updatedConfig);
      
      setBotConfigs(prev => {
        const index = prev.findIndex(b => b.bot_id === botId);
        if (index >= 0) {
          const updated = [...prev];
          updated[index] = updatedConfig;
          return updated;
        }
        return [...prev, updatedConfig];
      });
    } catch (error) {
      console.error('Error updating bot config:', error);
    }
  };

  const toggleKeyVisibility = (key) => {
    setShowKeys(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const apiKeyFields = [
    { key: 'twilio_account_sid', label: 'Twilio Account SID', description: 'For SMS & voice outreach' },
    { key: 'twilio_auth_token', label: 'Twilio Auth Token', description: 'Twilio authentication' },
    { key: 'twilio_phone_number', label: 'Twilio Phone Number', description: 'Your Twilio phone number' },
    { key: 'estated_api_key', label: 'Estated API Key', description: 'Property & owner data' },
    { key: 'regrid_api_key', label: 'Regrid API Key', description: 'Parcel boundaries & zoning' },
    { key: 'rentcast_api_key', label: 'RentCast API Key', description: 'Market rent & Section 8 data' },
    { key: 'skip_tracing_api_key', label: 'Skip Tracing API Key', description: 'Contact verification' },
    { key: 'stripe_api_key', label: 'Stripe API Key', description: 'Payment processing' }
  ];

  const botTypes = [
    { id: 'land', name: 'Land Acquisition Bot', description: '55-70% of comps, $5K+ spread' },
    { id: 'section8', name: 'Section 8 Bot', description: '<$5K down, <$600/month' },
    { id: 'multifamily', name: 'Multifamily Bot', description: 'Seller finance, JV deals' },
    { id: 'seller_finance', name: 'Seller Finance Bot', description: 'Creative structures' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="admin-settings-page">
      <div className="mb-8">
        <h1 className="text-4xl font-black tracking-tight mb-2" data-testid="page-title">Admin Settings</h1>
        <p className="text-muted-foreground">Configure API keys, bot rules, and system parameters</p>
      </div>

      {saveStatus && (
        <div className={`mb-6 p-4 rounded-lg flex items-center gap-2 ${saveStatus.type === 'success' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
          {saveStatus.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
          {saveStatus.message}
        </div>
      )}

      <Tabs defaultValue="api-keys" className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="api-keys" className="flex items-center gap-2">
            <Key size={16} /> API Keys
          </TabsTrigger>
          <TabsTrigger value="bot-config" className="flex items-center gap-2">
            <Bot size={16} /> Bot Configuration
          </TabsTrigger>
          <TabsTrigger value="scoring" className="flex items-center gap-2">
            <Scale size={16} /> Deal Scoring
          </TabsTrigger>
          <TabsTrigger value="budgets" className="flex items-center gap-2">
            <DollarSign size={16} /> Budgets
          </TabsTrigger>
        </TabsList>

        {/* API Keys Tab */}
        <TabsContent value="api-keys">
          <Card className="border-border">
            <CardHeader>
              <CardTitle>API Key Management</CardTitle>
              <CardDescription>Configure third-party service credentials. Keys are encrypted and masked for security.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {apiKeyFields.map((field) => (
                <div key={field.key} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-sm font-semibold">{field.label}</Label>
                      <p className="text-xs text-muted-foreground">{field.description}</p>
                    </div>
                    {settings[field.key] && (
                      <Badge variant="outline" className="text-green-400 border-green-400">Configured</Badge>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      type={showKeys[field.key] ? 'text' : 'password'}
                      value={settings[field.key] || ''}
                      onChange={(e) => setSettings({ ...settings, [field.key]: e.target.value })}
                      placeholder={`Enter ${field.label}`}
                      className="font-mono"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => toggleKeyVisibility(field.key)}
                    >
                      {showKeys[field.key] ? <EyeOff size={16} /> : <Eye size={16} />}
                    </Button>
                  </div>
                </div>
              ))}
              <Button onClick={saveApiKeys} disabled={saving} className="w-full bg-accent hover:bg-accent/90">
                <Save size={16} className="mr-2" /> {saving ? 'Saving...' : 'Save API Keys'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Bot Configuration Tab */}
        <TabsContent value="bot-config">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {botTypes.map((bot) => {
              const config = botConfigs.find(c => c.bot_id === bot.id) || {};
              return (
                <Card key={bot.id} className="border-border">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg">{bot.name}</CardTitle>
                        <CardDescription>{bot.description}</CardDescription>
                      </div>
                      <Switch
                        checked={config.enabled !== false}
                        onCheckedChange={(v) => updateBotConfig(bot.id, 'enabled', v)}
                      />
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label>Auto-Respond</Label>
                      <Switch
                        checked={config.auto_respond !== false}
                        onCheckedChange={(v) => updateBotConfig(bot.id, 'auto_respond', v)}
                      />
                    </div>
                    <div>
                      <Label>Max Daily Conversations</Label>
                      <Input
                        type="number"
                        value={config.max_daily_conversations || 50}
                        onChange={(e) => updateBotConfig(bot.id, 'max_daily_conversations', parseInt(e.target.value))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Max Daily Offers</Label>
                      <Input
                        type="number"
                        value={config.max_daily_offers || 20}
                        onChange={(e) => updateBotConfig(bot.id, 'max_daily_offers', parseInt(e.target.value))}
                        className="mt-1"
                      />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* Scoring Tab */}
        <TabsContent value="scoring">
          <Card className="border-border">
            <CardHeader>
              <CardTitle>Deal Scoring Weights</CardTitle>
              <CardDescription>Adjust how different factors contribute to the overall deal score</CardDescription>
            </CardHeader>
            <CardContent className="space-y-8">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>ARV Weight</Label>
                  <span className="font-mono text-primary">{((scoringWeights.arv_weight || 0.25) * 100).toFixed(0)}%</span>
                </div>
                <Slider
                  value={[(scoringWeights.arv_weight || 0.25) * 100]}
                  onValueChange={([v]) => setScoringWeights({ ...scoringWeights, arv_weight: v / 100 })}
                  max={50}
                  step={1}
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Cash Flow Weight</Label>
                  <span className="font-mono text-primary">{((scoringWeights.cash_flow_weight || 0.25) * 100).toFixed(0)}%</span>
                </div>
                <Slider
                  value={[(scoringWeights.cash_flow_weight || 0.25) * 100]}
                  onValueChange={([v]) => setScoringWeights({ ...scoringWeights, cash_flow_weight: v / 100 })}
                  max={50}
                  step={1}
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Location Weight</Label>
                  <span className="font-mono text-primary">{((scoringWeights.location_weight || 0.15) * 100).toFixed(0)}%</span>
                </div>
                <Slider
                  value={[(scoringWeights.location_weight || 0.15) * 100]}
                  onValueChange={([v]) => setScoringWeights({ ...scoringWeights, location_weight: v / 100 })}
                  max={50}
                  step={1}
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Condition Weight</Label>
                  <span className="font-mono text-primary">{((scoringWeights.condition_weight || 0.15) * 100).toFixed(0)}%</span>
                </div>
                <Slider
                  value={[(scoringWeights.condition_weight || 0.15) * 100]}
                  onValueChange={([v]) => setScoringWeights({ ...scoringWeights, condition_weight: v / 100 })}
                  max={50}
                  step={1}
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Motivation Weight</Label>
                  <span className="font-mono text-primary">{((scoringWeights.motivation_weight || 0.20) * 100).toFixed(0)}%</span>
                </div>
                <Slider
                  value={[(scoringWeights.motivation_weight || 0.20) * 100]}
                  onValueChange={([v]) => setScoringWeights({ ...scoringWeights, motivation_weight: v / 100 })}
                  max={50}
                  step={1}
                />
              </div>
              <Button onClick={saveScoringWeights} disabled={saving} className="w-full bg-accent hover:bg-accent/90">
                <Save size={16} className="mr-2" /> Save Scoring Weights
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Budgets Tab */}
        <TabsContent value="budgets">
          <Card className="border-border">
            <CardHeader>
              <CardTitle>Budget Thresholds</CardTitle>
              <CardDescription>Set acquisition rules and budget limits for each deal type</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4 p-4 bg-secondary/30 rounded-lg">
                  <h4 className="font-semibold text-green-400">Land Deals</h4>
                  <div>
                    <Label>Max Purchase Price</Label>
                    <Input
                      type="number"
                      value={budgetThresholds.land_max_price || 200000}
                      onChange={(e) => setBudgetThresholds({ ...budgetThresholds, land_max_price: parseInt(e.target.value) })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Minimum Assignment Spread</Label>
                    <Input
                      type="number"
                      value={budgetThresholds.land_min_spread || 5000}
                      onChange={(e) => setBudgetThresholds({ ...budgetThresholds, land_min_spread: parseInt(e.target.value) })}
                      className="mt-1"
                    />
                  </div>
                </div>

                <div className="space-y-4 p-4 bg-secondary/30 rounded-lg">
                  <h4 className="font-semibold text-blue-400">Section 8 Deals</h4>
                  <div>
                    <Label>Max Down Payment</Label>
                    <Input
                      type="number"
                      value={budgetThresholds.section8_max_down || 5000}
                      onChange={(e) => setBudgetThresholds({ ...budgetThresholds, section8_max_down: parseInt(e.target.value) })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Max Monthly Payment</Label>
                    <Input
                      type="number"
                      value={budgetThresholds.section8_max_monthly || 600}
                      onChange={(e) => setBudgetThresholds({ ...budgetThresholds, section8_max_monthly: parseInt(e.target.value) })}
                      className="mt-1"
                    />
                  </div>
                </div>

                <div className="space-y-4 p-4 bg-secondary/30 rounded-lg">
                  <h4 className="font-semibold text-purple-400">Multifamily Deals</h4>
                  <div>
                    <Label>Minimum Cap Rate (%)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={budgetThresholds.mfh_min_cap_rate || 6.0}
                      onChange={(e) => setBudgetThresholds({ ...budgetThresholds, mfh_min_cap_rate: parseFloat(e.target.value) })}
                      className="mt-1"
                    />
                  </div>
                </div>

                <div className="space-y-4 p-4 bg-secondary/30 rounded-lg">
                  <h4 className="font-semibold text-orange-400">General Budgets</h4>
                  <div>
                    <Label>Daily Marketing Budget</Label>
                    <Input
                      type="number"
                      value={budgetThresholds.daily_marketing_budget || 500}
                      onChange={(e) => setBudgetThresholds({ ...budgetThresholds, daily_marketing_budget: parseInt(e.target.value) })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Monthly Acquisition Budget</Label>
                    <Input
                      type="number"
                      value={budgetThresholds.monthly_acquisition_budget || 50000}
                      onChange={(e) => setBudgetThresholds({ ...budgetThresholds, monthly_acquisition_budget: parseInt(e.target.value) })}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
              <Button onClick={saveBudgetThresholds} disabled={saving} className="w-full bg-accent hover:bg-accent/90">
                <Save size={16} className="mr-2" /> Save Budget Thresholds
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminSettings;
